import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import {
  addDaysISO,
  formatDate,
  formatDateFull,
  formatMonthYear,
  isWeekend,
  monthGrid,
  nextMondayISO,
  nextWorkingDayISO,
  parseDate,
  toISODate,
  todayISO,
  WEEKDAYS_SHORT,
} from '../../lib/dates';
import type { Placement } from './layer';
import { Popover } from './Popover';

export type QuickDate = { label: string; value: string; hint?: string };

export function quickDates(): QuickDate[] {
  const today = todayISO();
  return [
    { label: 'Сьогодні', value: today },
    { label: 'Завтра', value: addDaysISO(today, 1) },
    { label: 'Наступний робочий день', value: nextWorkingDayISO(today) },
    { label: 'Наступний понеділок', value: nextMondayISO(today) },
  ];
}

/** Панель вибору дати: швидкі варіанти + місячна сітка з клавіатурною навігацією (role="grid"). */
export function DatePanel({
  value,
  onSelect,
  allowClear,
  clearLabel = 'Без строку',
  footer,
  min,
}: {
  value: string | null;
  onSelect: (v: string | null) => void;
  allowClear?: boolean;
  clearLabel?: string;
  footer?: ReactNode;
  min?: string;
}) {
  const today = todayISO();
  const [month, setMonth] = useState(() => (value ?? today).slice(0, 7) + '-01');
  const [focusDay, setFocusDay] = useState(value ?? today);
  const gridRef = useRef<HTMLTableElement>(null);
  const labelId = useId();
  const weeks = useMemo(() => monthGrid(month), [month]);
  const [keyNav, setKeyNav] = useState(false);
  const tabbableDay = focusDay.slice(0, 7) === month.slice(0, 7) ? focusDay : month;

  useEffect(() => {
    if (!keyNav) return;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${focusDay}"]`)?.focus();
  }, [focusDay, keyNav, month]);

  const moveFocus = (iso: string) => {
    setKeyNav(true);
    setFocusDay(iso);
    if (iso.slice(0, 7) !== month.slice(0, 7)) setMonth(iso.slice(0, 7) + '-01');
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const map: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in map) {
      e.preventDefault();
      moveFocus(addDaysISO(focusDay, map[e.key]!));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      moveFocus(toISODate(parseDate(focusDay).add(e.key === 'PageUp' ? -1 : 1, 'month')));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const wd = parseDate(focusDay).isoWeekday();
      moveFocus(addDaysISO(focusDay, e.key === 'Home' ? 1 - wd : 7 - wd));
    }
  };

  return (
    <div className="flex flex-col sm:flex-row">
      <div className="flex flex-row flex-wrap gap-1 border-b border-line p-2 sm:w-[190px] sm:flex-col sm:flex-nowrap sm:border-r sm:border-b-0">
        {quickDates().map((q) => (
          <button
            key={q.label}
            type="button"
            onClick={() => onSelect(q.value)}
            disabled={!!min && q.value < min}
            className={cn(
              'flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-meta hover:bg-surface-2 disabled:opacity-40',
              value === q.value && 'bg-accent-soft text-accent-fg',
            )}
          >
            <span className="font-medium">{q.label}</span>
            <span className="tnum text-caption text-fg-3">{formatDate(q.value)}</span>
          </button>
        ))}
        {allowClear && (
          <button type="button" onClick={() => onSelect(null)} className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-meta text-fg-2 hover:bg-surface-2">
            <X className="size-3.5" aria-hidden />
            {clearLabel}
          </button>
        )}
      </div>
      <div className="p-2">
        <div className="mb-1 flex items-center justify-between">
          <span id={labelId} className="pl-1 text-body font-semibold" aria-live="polite">
            {formatMonthYear(month)}
          </span>
          <span className="flex">
            <button
              type="button"
              aria-label="Попередній місяць"
              onClick={() => setMonth(toISODate(parseDate(month).subtract(1, 'month')))}
              className="inline-flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-surface-2"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Наступний місяць"
              onClick={() => setMonth(toISODate(parseDate(month).add(1, 'month')))}
              className="inline-flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-surface-2"
            >
              <ChevronRight className="size-4" aria-hidden />
            </button>
          </span>
        </div>
        <table ref={gridRef} role="grid" aria-labelledby={labelId} onKeyDown={onKeyDown} className="border-collapse">
          <thead>
            <tr>
              {WEEKDAYS_SHORT.map((d, i) => (
                <th key={d} scope="col" className={cn('h-7 w-8 text-caption font-medium', i >= 5 ? 'text-fg-3' : 'text-fg-2')}>
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w[0]}>
                {w.map((d) => {
                  const inMonth = d.slice(0, 7) === month.slice(0, 7);
                  const isSel = d === value;
                  const isToday = d === today;
                  const disabled = !!min && d < min;
                  return (
                    <td key={d} role="gridcell" aria-selected={isSel} className="p-px">
                      <button
                        type="button"
                        data-day={d}
                        tabIndex={d === tabbableDay ? 0 : -1}
                        disabled={disabled}
                        aria-label={`${formatDateFull(d)}${isToday ? ', сьогодні' : ''}`}
                        aria-current={isToday ? 'date' : undefined}
                        onClick={() => onSelect(d)}
                        onFocus={() => setFocusDay(d)}
                        className={cn(
                          'tnum relative flex size-8 items-center justify-center rounded-sm text-meta transition-colors',
                          !inMonth && 'text-fg-3',
                          inMonth && isWeekend(d) && !isSel && 'text-fg-2',
                          isSel ? 'bg-accent font-semibold text-on-accent' : 'hover:bg-surface-2',
                          isToday && !isSel && 'font-semibold text-accent-fg ring-1 ring-accent-soft-2 ring-inset',
                          disabled && 'cursor-not-allowed opacity-35',
                        )}
                      >
                        {Number(d.slice(8, 10))}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {footer}
      </div>
    </div>
  );
}

/** Поле дати для форм: кнопка з датою й спливна панель. */
export function DateField({
  value,
  onChange,
  allowClear,
  placeholder = 'Оберіть дату',
  invalid,
  id,
  placement = 'bottom-start',
  describedBy,
  disabled,
  ...aria
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  allowClear?: boolean;
  placeholder?: string;
  invalid?: boolean;
  id?: string;
  placement?: Placement;
  describedBy?: string;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  required?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const valueId = useId();
  return (
    <>
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        aria-invalid={invalid || aria['aria-invalid'] || undefined}
        aria-describedby={[valueId, describedBy ?? aria['aria-describedby']].filter(Boolean).join(' ')}
        onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}
        className={cn(
          'inline-flex h-[var(--control-md)] w-full items-center gap-2 rounded-sm border border-line-input bg-surface px-2.5 text-left text-body transition-colors hover:border-fg-3',
          'aria-[invalid=true]:border-danger disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <CalendarDays className="size-4 shrink-0 text-fg-3" aria-hidden />
        <span id={valueId} className={cn('tnum flex-1 truncate', !value && 'text-fg-3')}>
          {value ? formatDateFull(value) : placeholder}
        </span>
      </button>
      <Popover open={!!anchor} onClose={() => setAnchor(null)} anchor={anchor} placement={placement} aria-label="Вибір дати" className="max-w-[calc(100vw-16px)]">
        <DatePanel
          value={value}
          allowClear={allowClear}
          onSelect={(v) => {
            onChange(v);
            setAnchor(null);
          }}
        />
      </Popover>
    </>
  );
}
